<template>
  <div ref="container" class="immunizations-editor">
    <el-dialog v-model="dialogVisibility" :title="title" :close-on-click-modal="false" :close-on-press-escape="false" :show-close="false" top="5vh" :size="reviewingImmunizations ? 'large' : 'small'">
      <template v-if="reviewingImmunizations === false">
        <el-form v-loading="dialogBusy" ref="editorForm" :model="formModel" :rules="formRules" label-width="120px">
          <template v-if="isBatch">
            <el-form-item v-if="hasFacilityTypeFilter()" label="Facility Type" prop="selectedFacType" :rules="[{ required: true, message: 'Facility type is required' }]">
              <el-select :disabled="disableForPCCResidents()" v-model="formModel.selectedFacType">
                <el-option v-for="item in ['RET', 'LTC']" :key="item" :label="item" :value="item"> </el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="Facility" prop="selectedFacId" :rules="[{ required: true, message: 'Facility is required' }]">
              <el-select v-model="formModel.selectedFacId" :disabled="hasFacilityTypeFilter() && !formModel.selectedFacType && disableForPCCResidents()">
                <el-option v-for="item in availableSites" :key="item.id" :label="item.displayText" :value="item.id"> </el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="Unit" prop="selectedUnitId" v-if="isStaff === false">
              <el-select v-model="formModel.selectedUnitId" :disabled="!formModel.selectedFacId && disableForPCCResidents()">
                <el-option :key="'All'" :value="'All'">All</el-option>
                <el-option v-for="item in availableUnits" :key="item.id" :label="item.displayText" :value="item.id"> </el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="Individuals" class="individuals" prop="selectedIndividuals" :rules="[{ required: isStaff ? !!formModel.selectedFacId : !!formModel.selectedUnitId, validator: validateIndividuals }]">
              <div class="select-offset">
                <el-select v-model="formModel.selectedIndividuals" :multiple="true" :clearable="true" :filterable="true" :disabled="disableIndividualPicker() && disableForPCCResidents()" placeholder="Select individuals" style="width: 400px; max-width: 100%">
                  <el-option v-for="item in availableIndividuals" :key="item.id" :label="`${item.firstName} ${item.lastName} (${item.id})`" :value="item.id"> </el-option>
                </el-select>
              </div>
              <div>
                <el-button data-testid="select-all-individuals" :disabled="disableForPCCResidents()" @click="selectAllIndividuals" size="mini">Select All Individuals</el-button>
                <el-button data-testid="clear-individuals" :disabled="disableForPCCResidents()" @click="clearAllIndividuals" size="mini">Clear</el-button>
              </div>
            </el-form-item>
          </template>
          <!-- Immunization Type -->
          <el-form-item label="Immunization Type" prop="description">
            <el-select :disabled="disableForPCCResidents()" v-model="formModel.description" filterable>
              <el-option v-for="item in immunizationTypes" :key="item" :label="item" :value="item"> </el-option>
            </el-select>
          </el-form-item>
          <!-- Immunization Administration Date -->
          <el-form-item label="Administration Date" prop="date">
            <el-date-picker :disabled="disableForPCCResidents()" v-model="formModel.date" type="date" :format="$configStore.datePickerDateFormat()" placeholder="Select date" :picker-options="administrationPickerOptions"> </el-date-picker>
          </el-form-item>
          <!-- Immunization Expiry Date -->
          <el-form-item label="Expiry Date" prop="expiry">
            <el-date-picker v-model="formModel.expiry" type="date" :format="$configStore.datePickerDateFormat()" :pickerOptions="expiryPickerOptions" placeholder="Select date"> </el-date-picker>
          </el-form-item>
          <!-- Declined -->
          <el-form-item label="Declined?" prop="declined">
            <el-select :disabled="disableForPCCResidents()" v-model="formModel.declined">
              <el-option label="Yes" :value="true"></el-option>
              <el-option label="No" :value="false"></el-option>
            </el-select>
          </el-form-item>
          <!-- Declined Reason -->
          <el-form-item v-if="formModel.declined === true" label="Reason" prop="declinedReason">
            <el-select :disabled="disableForPCCResidents()" v-model="formModel.declinedReason" filterable>
              <el-option v-for="item in declinedReasons" :key="item" :label="item" :value="item"> </el-option>
            </el-select>
          </el-form-item>
          <!-- Vaccine Type -->
          <el-form-item v-if="formModel.declined === false" label="Vaccine Type" prop="vaccineType">
            <el-select :disabled="disableForPCCResidents()" v-model="formModel.vaccineType" filterable>
              <el-option v-for="item in vaccineTypes" :key="item" :label="item" :value="item"> </el-option>
            </el-select>
          </el-form-item>
          <!-- Immunization location -->
          <el-form-item v-if="formModel.declined === false" label="Location" prop="location">
            <el-select :disabled="disableForPCCResidents()" v-model="formModel.location" filterable>
              <el-option v-for="item in locations" :key="item" :label="item" :value="item"> </el-option>
            </el-select>
          </el-form-item>
          <!-- Lot Number -->
          <el-form-item v-if="formModel.declined === false" label="Lot Number" prop="lotNumber">
            <el-input :disabled="disableForPCCResidents()" placeholder="Lot Number" v-model="formModel.lotNumber"></el-input>
          </el-form-item>
          <!-- Notes -->
          <el-form-item label="Notes" prop="note">
            <el-input :disabled="disableForPCCResidents()" type="textarea" :rows="3" :maxlength="notesMaxLength" placeholder="Notes" v-model="formModel.note"></el-input>
            <character-count :limit="notesMaxLength" :count="formModel.note.length" />
          </el-form-item>
          <div class="dialog-footer">
            <el-row>
              <el-button data-testid="cancel-immunization" @click="cancelForm" :disabled="dialogBusy">Cancel</el-button>
              <el-button data-testid="save-immunization" type="primary" v-if="isBatch === false" @click="commitForm(false)" :disabled="dialogBusy">{{ submitButton }}</el-button>
              <el-button data-testid="review-immunizations" type="primary" v-else @click="reviewImmunizations()" :disabled="dialogBusy">Review Immunizations</el-button>
            </el-row>
          </div>
        </el-form>
      </template>
      <template v-if="reviewingImmunizations">
        <div class="error-banner" v-if="reviewingImmunizationErrorIndexes.length">
          Please review any errors below:
        </div>
        <el-table :data="reviewModels" height="500" v-loading="dialogBusy" :row-class-name="highlightBatchErrors">
          <el-table-column label="Name" fixed="left" width="200" class-name="name">
            <template scope="scope"> {{ scope.row._clientModel.firstName }} {{ scope.row._clientModel.lastName }} </template>
          </el-table-column>
          <el-table-column label="Immunization Type" width="250">
            <template scope="scope">
              <el-select v-model="scope.row.description" filterable disabled>
                <el-option v-for="item in immunizationTypes" :key="item" :label="item" :value="item"> </el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="Administration Date" width="210">
            <template scope="scope">
              <el-date-picker
                v-model="scope.row.date"
                type="date"
                :format="$configStore.datePickerDateFormat()"
                :pickerOptions="{
                  disabledDate: input => {
                    return disableDatesForAdmin(input, scope.row.expiry);
                  }
                }"
              ></el-date-picker>
            </template>
          </el-table-column>
          <el-table-column label="Expiry Date" width="210">
            <template scope="scope">
              <el-date-picker
                v-model="scope.row.expiry"
                type="date"
                :format="$configStore.datePickerDateFormat()"
                :pickerOptions="{
                  disabledDate: input => {
                    return disableDatesForExpiry(input, scope.row.date);
                  }
                }"
              ></el-date-picker>
            </template>
          </el-table-column>
          <el-table-column label="Declined" width="150">
            <template scope="scope">
              <el-select v-model="scope.row.declined" @change="declineChangeHandler($event, scope.row)">
                <el-option label="Yes" :value="true"></el-option>
                <el-option label="No" :value="false"></el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="Reason" width="150">
            <template scope="scope">
              <el-select v-if="scope.row.declined" v-model="scope.row.declinedReason" filterable>
                <el-option v-for="item in declinedReasons" :key="item" :label="item" :value="item"> </el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="Vaccine Type" width="150">
            <template scope="scope">
              <el-select v-if="!scope.row.declined" v-model="scope.row.vaccineType" filterable>
                <el-option v-for="item in vaccineTypes" :key="item" :label="item" :value="item"> </el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="Location" width="150">
            <template scope="scope">
              <el-select v-if="!scope.row.declined" v-model="scope.row.location" filterable>
                <el-option v-for="item in locations" :key="item" :label="item" :value="item"> </el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="Lot Number" width="250">
            <template scope="scope">
              <el-input v-if="!scope.row.declined" v-model="scope.row.lotNumber"></el-input>
            </template>
          </el-table-column>
          <el-table-column label="Notes" width="300">
            <template scope="scope">
              <el-input v-model="scope.row.note"></el-input>
            </template>
          </el-table-column>
        </el-table>
        <div class="dialog-footer">
          <el-row>
            <el-button data-testid="cancel-review" @click="reviewingImmunizations = false">Cancel</el-button>
            <el-button data-testid="submit-batch-immunizations" @click="submitBatchImmunizations()">Proceed</el-button>
          </el-row>
        </div>
      </template>
    </el-dialog>
  </div>
</template>
<script>
const validateNotEmpty = (rule, value, callback) => {
  if (!value) {
    callback("This field cannot be empty");
  }
  callback();
};
const validateBoolean = (rule, value, callback) => {
  if (typeof value !== "boolean") {
    callback("This field cannot be empty");
  }
  callback();
};

import CharacterCount from "../Shared/CharacterCount.vue";
import { Loading } from "element-ui";
import auth from "../../auth";
import util from "../../util";
import moment from "moment";

export default {
  name: "immunization-editor",
  props: ["title", "immunization", "clientId", "isBatch", "isStaff", "facId"],
  components: {
    "character-count": CharacterCount
  },
  data() {
    return {
      isEdit: false,
      submitButton: "",
      notesMaxLength: 250,
      dialogVisibility: true,
      dialogBusy: true,
      administrationPickerOptions: {
        disabledDate: input => {
          // disable dates after current date and after expiry date
          // add 12 hours to correct element-ui's DST offset (cells passed as 23:00 previous day after fallback)
          const currentDate = moment().startOf("day");
          const datepickerDate = moment(input).add(12, "hours").startOf("day");
          return datepickerDate.isAfter(currentDate) || (this.formModel.expiry && datepickerDate.isAfter(moment(this.formModel.expiry).startOf("day")));
        }
      },
      expiryPickerOptions: {
        disabledDate: input => {
          // disable dates before administration date
          const currentDate = moment().startOf("day");
          const datepickerDate = moment(input).startOf("day");
          return datepickerDate.isBefore(this.formModel.date ? moment(this.formModel.date).startOf("day") : currentDate);
        }
      },
      facilities: [],
      immunizationConfig: {},
      immunizationTypes: [],
      vaccineTypes: [],
      declinedReasons: [],
      allIndividuals: [],
      availableIndividuals: [],
      locations: [],
      formRules: {
        description: [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }],
        date: [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }],
        declined: [{ required: true, validator: validateBoolean, message: "This field  is required", trigger: "blur" }]
      },
      formModel: {
        description: null,
        date: null,
        expiry: null,
        declined: null,
        declinedReason: null,
        location: null,
        vaccineType: null,
        lotNumber: null,
        note: "",
        selectedFacType: null,
        selectedFacId: null,
        selectedUnitId: null,
        selectedIndividuals: []
      },
      reviewingImmunizations: false,
      reviewingImmunizationErrorIndexes: [],
      reviewModels: []
    };
  },
  watch: {
    dialogVisibility(val) {
      if (val === false) {
        this.cancelForm();
      }
    },
    "formModel.selectedFacType": function() {
      this.formModel.selectedFacId = null;
      this.formModel.selectedUnitId = null;
      this.formModel.selectedIndividuals = [];
    },
    "formModel.selectedFacId": function() {
      this.formModel.selectedIndividuals = [];
      this.getIndividualsForSelectedFac();
    },
    "formModel.selectedUnitId": function(value) {
      this.formModel.selectedIndividuals = [];
      if (value === "All") {
        this.availableIndividuals = this.allIndividuals;
      } else {
        this.availableIndividuals = this.allIndividuals.filter(person => person.unitId === value);
      }
    },
    "formModel.description"(val) {
      if (this.immunizationConfig) {
        this.vaccineTypes = this.immunizationConfig[val] ? this.immunizationConfig[val] : ["Unknown", "Other", "Not Applicable"];
      } else {
        // this is a fallback if imm config is not setup in DB
        if (!this.isRevera) {
          return;
        }
        if (value === "COVID-19") {
          this.locations = ["Unknown", "Left Deltoid", "Right Deltoid"];
          this.vaccineTypes = [
            "Unknown",
            "AstraZeneca COVID-19 Dose 1",
            "AstraZeneca COVID-19 Dose 2",
            "AstraZeneca COVID-19 Dose 3",
            "COMIRNATY Bivalent Original / Omicron BA.4/BA.5",
            "COMIRNATY Bivalent Booster #2",
            "Covifenz COVID-19 Dose 1",
            "Covifenz COVID-19 Dose 2",
            "Janssen COVID-19",
            "Moderna COVID-19 Dose 1",
            "Moderna COVID-19 Dose 2",
            "Moderna COVID-19 Dose 3",
            "Moderna COVID-19 Dose 4",
            "Moderna COVID-19 Dose 5",
            "Pfizer COVID-19 Dose 1",
            "Pfizer COVID-19 Dose 2",
            "Pfizer COVID-19 Dose 3",
            "Pfizer COVID-19 Dose 4",
            "Pfizer COVID-19 Dose 5",
            "Nuvaxovid COVID-19 Dose 1",
            "Nuvaxovid COVID-19 Dose 2",
            "SPIKEVAX Bivalent Vaccine",
            "SPIKEVAX Bivalent Original / Omicron BA.4/5",
            "SPIKEVAX Bivalent Booster #2"
          ];
        } else if (value === "Influenza") {
          this.locations = ["Unknown", "Left Deltoid", "Right Deltoid"];
          this.vaccineTypes = ["Unknown", "Afluria Tetra", "Fluad", "Flucelvax QUAD", "Flulaval Tetra", "Fluzone High-Dose QUAD", "Fluzone Quad", "Supemtek"];
        } else {
          this.locations = util.sortWithFixedLeadingElements(this.config.ImmunizationLoc || [], "Unknown");
          this.vaccineTypes = ["Not Available", "Other", "Unknown", "Not Applicable"];
        }
      }
    },
    "formModel.declined"(val) {
      if (val === true) {
        // when declined, declinedReason is required & vaccineType/location must be null
        this.formRules.declinedReason = [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }];
        delete this.formRules.vaccineType;
        delete this.formRules.location;
        this.formModel.vaccineType = null;
        this.formModel.location = null;
      } else if (val === false) {
        // when not declined, location is required & declinedReason must be null;
        // vaccineType is required only when the org config demands it (HCI-4792)
        this.formRules.vaccineType = this.$configStore.vaccineTypeRequired()
          ? [
              {
                required: true,
                validator: validateNotEmpty,
                message: "This field  is required",
                trigger: "blur",
              },
            ]
          : [];
        if (this.$configStore.immunizationLocationRequired()) {
          this.formRules.location = [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }];
        }
        delete this.formRules.declinedReason;
        this.formModel.declinedReason = null;
      } else {
        // reset declined conditions when empty
        delete this.formRules.declinedReason;
        delete this.formRules.vaccineType;
        delete this.formRules.location;
        this.formModel.declinedReason = null;
        this.formModel.vaccineType = null;
        this.formModel.location = null;
      }
      this.updateFormRulesForPCC();
    }
  },
  computed: {
    availableSites() {
      const type = this.formModel.selectedFacType;
      if (type) {
        return (this.sites || []).filter(fac => (fac.unitType === null && type === "LTC") || fac.unitType === type);
      } else {
        return this.sites;
      }
    },
    availableUnits() {
      let units = [];
      if (this.formModel.selectedFacId) {
        const fac = this.availableSites.filter(fac => fac.id === this.formModel.selectedFacId)[0];
        if (fac) {
          units = (fac.units || []).filter(unit => unit.code !== "PLACE_HOLDER" && unit.archived !== true);
        }
      }
      return units;
    }
  },
  methods: {
    disableDatesForExpiry(input, admin) {
      return moment(input).isBefore(admin);
    },
    disableDatesForAdmin(input, expiry) {
      return moment(input).isAfter(expiry);
    },
    async getImmunizationConfig() {
      try {
        const result = await this.$http.get(`${window.CONFIG.immunization_api}/config`);
        // Deploy-order safety (HCI-4864): an old API returns the config flat
        // rather than nested under `immunizationConfig` (same reader as FiltersSection).
        return result.data.immunizationConfig ?? result.data;
      } catch (err) {
        return null; // see created() for temporary workaround to use the old config if imm config db not setup
      }
    },
    setupForm() {
      // setup form either for editing an existing immunization or creating a immunization record/batch
      if (this.isEdit) {
        this.submitButton = "Update";
        this.formModel.description = this.immunization.description;
        this.formModel.date = this.immunization.date ? moment.utc(this.immunization.date).format(this.$configStore.dateFormat()) : null;
        this.formModel.expiry = this.immunization.expiry ? moment.utc(this.immunization.expiry).format(this.$configStore.dateFormat()) : null;
        this.formModel.declined = this.immunization.declined;
        this.formModel.declinedReason = this.immunization.declinedReason;
        this.formModel.location = this.immunization.location;
        this.formModel.vaccineType = this.immunization.vaccineType;
        this.formModel.lotNumber = this.immunization.lotNumber;
        this.formModel.note = this.immunization.note || "";
      } else {
        this.submitButton = "Add";
      }
    },
    cancelForm() {
      this.$emit("immunization-editor-close");
    },
    async commitForm() {
      this.$refs.editorForm.validate(async valid => {
        if (valid) {
          try {
            this.dialogBusy = true;
            // setup request body
            const raw = { ...this.formModel };
            const immunization = {
              description: raw.description,
              date: raw.date,
              expiry: raw.expiry || null,
              declined: raw.declined,
              declinedReason: raw.declinedReason,
              location: raw.location,
              vaccineType: raw.vaccineType,
              lotNumber: raw.lotNumber,
              note: raw.note
            };
            let body;
            if (this.isBatch) {
              const clientIds = raw.selectedIndividuals;
              body = {
                immunization,
                clientIds
              };
            } else {
              immunization.clientId = this.clientId;
              body = immunization;
            }
            // create or update immunization
            let result;
            if (this.isEdit) {
              result = await this.$http.patch(`${window.CONFIG.immunization_api}/${this.immunization.id}`, body);
            } else {
              result = await this.$http.post(`${window.CONFIG.immunization_api}/${this.isBatch ? "batch" : ""}`, body);
            }
            console.log({ result });
            this.dialogBusy = false;
            this.$emit("immunization-editor-save");
          } catch (error) {
            this.$emit("immunization-editor-error");
          }
        } else {
          return false;
        }
      });
    },
    hasFacilityTypeFilter() {
      return auth.userInfo().roles.indexOf("ROLE_LTC") >= 0 && auth.userInfo().roles.indexOf("ROLE_RET") >= 0;
    },
    validateIndividuals(rule, value, callback) {
      if (this.disableIndividualAll() && this.disableIndividualPicker()) {
        callback();
      } else if (this.formModel.selectedIndividuals.length === 0) {
        callback(new Error("Select all or some individuals"));
      } else {
        callback();
      }
    },
    disableIndividualAll() {
      return this.isStaff ? !this.formModel.selectedFacId : !this.formModel.selectedUnitId;
    },
    disableIndividualPicker() {
      return this.isStaff ? !this.formModel.selectedFacId : !this.formModel.selectedUnitId;
    },
    disableForPCCResidents() {
      if (this.isStaff) return false;
      const targetFacId = this.isBatch ? this.formModel.selectedFacId : this.facId;
      return this.$configStore.isPccFacility(targetFacId);
    },
    async getIndividualsForSelectedFac() {
      if (this.formModel.selectedFacId) {
        const loadingInstance = Loading.service({ fullscreen: true });
        const clientType = `${this.isStaff ? "Staff" : "Client"}`;
        const response = await this.$http.get(`${window.CONFIG.legacy_facade_api}/clients/list/unit/0/page/0?clientType=${clientType}&size=10000&sort=firstName&facIds=${this.formModel.selectedFacId}`);
        loadingInstance.close();
        const isActive = i => (this.isStaff ? i.status === "C" : i.status !== "T" && i.status !== "D");
        this.allIndividuals = response.body.content.filter(isActive);
        this.availableIndividuals = this.allIndividuals;
      }
    },
    selectAllIndividuals() {
      this.formModel.selectedIndividuals = this.availableIndividuals.map(i => i.id);
    },
    clearAllIndividuals() {
      this.formModel.selectedIndividuals = [];
    },
    relocate() {
      if (this.$refs.container) {
        document.body.append(this.$refs.container);
      }
    },
    validateExpiryDate(rule, value, callback) {
      if (this.formModel.date && value && moment(value).isBefore(moment(this.formModel.date))) {
        callback(new Error("Expiry date cannot be before the administration date"));
      } else {
        callback();
      }
    },
    declineChangeHandler(isDeclined, row) {
      if (isDeclined) {
        row.vaccineType = null;
        row.location = null;
      } else {
        row.declinedReason = null;
      }
    },
    reviewImmunizations() {
      this.$refs.editorForm.validate(async valid => {
        if (valid) {
          const clientLookup = new Map(this.availableIndividuals.map(({ id, ...client }) => [id, client]));
          const { selectedIndividuals, description, date, expiry, declined, declinedReason, location, vaccineType, lotNumber, note } = this.formModel;
          this.reviewModels = selectedIndividuals.map(clientId => ({ description, date, expiry, declined, declinedReason, location, vaccineType, lotNumber, note, clientId, _clientModel: clientLookup.get(clientId) }));
          this.reviewingImmunizations = true;
        } else {
          return false;
        }
      });
    },
    highlightBatchErrors(row, index) {
      return this.reviewingImmunizationErrorIndexes.includes(index) ? "batch-error" : "";
    },
    batchImmunizationErrors(immunizations) {
      // Mirrors formRules: a declined row needs a reason and no vaccine type; an
      // administered row needs a vaccine type only when the org config demands it (HCI-4792).
      const vaccineTypeRequired = this.$configStore.vaccineTypeRequired();
      return immunizations
        .map(({ vaccineType, declined, declinedReason }, index) => {
          const validDeclined = declined && declinedReason && !vaccineType;
          const validAdministered = !declined && !declinedReason && (vaccineType || !vaccineTypeRequired);
          return validDeclined || validAdministered ? null : index;
        })
        .filter(row => row !== null);
    },
    async submitBatchImmunizations() {
      this.dialogBusy = true;
      const immunizations = this.reviewModels.map(({ description, date, expiry, declined, declinedReason, location, vaccineType, lotNumber, note, clientId }) => {
        return { description, date, expiry, declined, declinedReason, location, vaccineType, lotNumber, note, clientId };
      });
      this.reviewingImmunizationErrorIndexes = this.batchImmunizationErrors(immunizations);
      if (this.reviewingImmunizationErrorIndexes.length === 0) {
        await this.$http.post(`${window.CONFIG.immunization_api}/discrete-batch`, immunizations);
        this.reviewingImmunizations = false;
        this.reviewingImmunizationErrorIndexes = [];
        this.$emit("immunization-editor-save");
      }
      this.dialogBusy = false;
    },
    // disable form rules for residents when PCC integration is enabled (only expiry is editable in PCC mode)
    updateFormRulesForPCC() {
      if (this.disableForPCCResidents()) {
        this.formRules = {};
      }
    },
  },
  async created() {
    this.sites = this.$configStore.data.sites || [];
    this.units = (this.$configStore.data.units || []).filter(unit => unit.code !== "PLACE_HOLDER");
    this.isEdit = !!this.immunization.id;
    this.isRevera = auth.userInfo().org === "revera" || auth.userInfo().org === "revdemo";
    this.config = this.$configStore.data.config || {};
    this.declinedReasons = (this.config.DeclinedReason || []).sort();
    this.locations = util.sortWithFixedLeadingElements(this.config.ImmunizationLoc || [], "Unknown");
    const immunizationConfig = await this.getImmunizationConfig();
    if (immunizationConfig) {
      this.immunizationConfig = immunizationConfig;
      this.immunizationTypes = Object.keys(immunizationConfig);
    } else {
      // this is a fallback if imm config is not setup in DB
      this.immunizationConfig = null;
      this.immunizationTypes = (this.config.ImmunizationDesc || []).sort();
      this.vaccineTypes = util.sortWithFixedLeadingElements(this.config.VaccineTypes || [], "Not Available", "Other", "Unknown");
      console.log({ vaccs: this.vaccineTypes, imms: this.immunizationTypes });
    }
    this.setupForm();
    this.updateFormRulesForPCC();
    this.dialogBusy = false;
  },
  mounted() {
    this.relocate();
  }
};
</script>
<style scoped>
.individuals .el-select {
  width: 80%;
}
h2 {
  margin: 0 0 1rem 0;
}
.error-banner {
  padding: 0.5rem;
  background: #ebc5c5;
  color: #c00;
  margin-top: -1rem;
}
</style>
<style>
.batch-error td.name {
  color: #c00;
  background: #ebc5c5;
}
</style>
